import React, { memo, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Check,
  Users
} from 'lucide-react';
import { Clinician } from '../../types';
  
interface ClinicianTableProps {
  clinicians: Clinician[];
  onStatusToggle?: (clinicianId: string, newStatus: 'active' | 'inactive') => Promise<void>;
  showBulkEdit?: boolean;
  selectedClinicians?: string[];
  onSelectClinician?: (clinicianId: string) => void;
  onSelectAll?: () => void;
}

interface BulkUpdateData {
  status?: 'active' | 'inactive';
  ageRange?: {
    min: number;
    max: number;
  };
  location?: {
    city: string;
    state: string;
    zipCode: string;
  };
  insurance?: string[];
}

interface StatusConfig {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  textColor: string;
  label: string;
}

// Status configuration for better maintainability
const STATUS_CONFIG: Record<string, StatusConfig> = {
  active: {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'bg-emerald-500',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    label: 'Active'
  },
  verified: {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'bg-emerald-500',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    label: 'Verified'
  },
  inactive: {
    icon: <XCircle className="w-4 h-4" />,
    color: 'bg-gray-500',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    label: 'Inactive'
  },
  pending: {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    label: 'Pending'
  }
};

// Common insurance providers
const INSURANCE_OPTIONS = [
  'Aetna', 'Anthem', 'Blue Cross Blue Shield', 'Cigna', 'Humana',
  'Kaiser Permanente', 'Medicaid', 'Medicare', 'UnitedHealth',
  'Tricare', 'Self-Pay'
];

// Animation variants for performance
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" }
  }
};

// Define processed clinician type
interface ProcessedClinician extends Clinician {
  displayName: string;
  displayFirstName: string;
  displayLastName: string;
  initials: string;
  statusConfig: StatusConfig;
  specialtyDisplay: string;
  isActive: boolean;
  availabilityDisplay?: string;
  availabilityMetrics: {
    openSlots: number;
    bookedSlots: number;
    cancelledSlots: number;
    outOfOfficeSlots: number;
    blockedSlots: number;
    totalSlots: number;
    availabilityPercentage: number;
  };
  profileCompletionPercentage: number;
}

export const ClinicianTable: React.FC<ClinicianTableProps> = memo(({ 
  clinicians, 
  onStatusToggle,
  showBulkEdit = false,
  selectedClinicians = [],
  onSelectClinician,
  onSelectAll
}) => {
  const navigate = useNavigate();
  const [togglingStatus, setTogglingStatus] = useState<Set<string>>(new Set());

  // Memoize processed clinicians data
  const processedClinicians = useMemo((): ProcessedClinician[] => {
    return clinicians.map(clinician => {
      // Handle both camelCase and snake_case field names
      const displayFirstName = (clinician.firstName || clinician.first_name || clinician.name?.split(' ')[0] || '') as string;
      const displayLastName = (clinician.lastName || clinician.last_name || clinician.name?.split(' ').slice(1).join(' ') || '') as string;
      
      // Process availability data from new structure
      const availability = clinician.availability;
      const openSlots = availability?.open?.length || 0;
      const bookedSlots = availability?.booked?.length || 0;
      const cancelledSlots = availability?.cancelled?.length || 0;
      const outOfOfficeSlots = availability?.outOfOffice?.length || 0;
      const blockedSlots = availability?.blocked?.length || 0;
      const totalSlots = openSlots + bookedSlots + cancelledSlots + outOfOfficeSlots + blockedSlots;
      const computedAvailabilityPct = totalSlots > 0 ? Math.round((openSlots / totalSlots) * 100) : 0;
      // Prefer API availabilityRatio when present (number 0–100), else use computed %
      const availabilityPercentage = typeof (clinician as { availabilityRatio?: number }).availabilityRatio === 'number'
        ? (clinician as { availabilityRatio: number }).availabilityRatio
        : computedAvailabilityPct;

      // Completion: API sends profileCompletionRatio (0–100)
      const profileCompletionRatio = (clinician as { profileCompletionRatio?: number }).profileCompletionRatio;
      const profileCompletionPercentage = typeof profileCompletionRatio === 'number' ? profileCompletionRatio : (typeof clinician.profileCompletionPercentage === 'number' ? clinician.profileCompletionPercentage : 0);

      // Use isActive field from backend (boolean: true/false)
      // Fallback to other fields only if isActive is not provided (for backward compatibility)
      const isActive = clinician.isActive !== undefined 
        ? clinician.isActive 
        : (clinician.enabled !== undefined ? clinician.enabled : false);
      
      return {
        ...clinician,
        displayName: clinician.fullName || clinician.name || `${displayFirstName} ${displayLastName}`.trim(),
        displayFirstName,
        displayLastName,
        initials: getInitials(clinician),
        statusConfig: STATUS_CONFIG[isActive ? 'active' : 'inactive'],
        specialtyDisplay: Array.isArray(clinician.specialties) 
          ? clinician.specialties.slice(0, 2).join(', ') + (clinician.specialties.length > 2 ? '...' : '')
          : clinician.specialties || 'No specialty listed',
        isActive,
        availabilityDisplay: openSlots > 0 ? `${openSlots} open` : (totalSlots > 0 ? 'No open slots' : 'No data'),
        availabilityMetrics: {
          openSlots,
          bookedSlots,
          cancelledSlots,
          outOfOfficeSlots,
          blockedSlots,
          totalSlots,
          availabilityPercentage
        },
        profileCompletionPercentage
      };
    });
  }, [clinicians]);



  const handleSelectClinician = useCallback((id: string) => {
    onSelectClinician?.(id);
  }, [onSelectClinician]);

  const handleStatusToggle = useCallback(async (clinicianId: string, currentStatus: boolean) => {
    
    if (!onStatusToggle) {
      return;
    }
    
    setTogglingStatus(prev => new Set(prev).add(clinicianId));
    try {
      const newStatus = currentStatus ? 'inactive' : 'active';
      await onStatusToggle(clinicianId, newStatus);
    } catch (error) {
      console.error('Failed to toggle status:', error);
    } finally {
      setTogglingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(clinicianId);
        return newSet;
      });
    }
  }, [onStatusToggle]);





  if (clinicians.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-16"
      >
        <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Eye className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500 text-lg font-medium">No clinicians found</p>
        <p className="text-gray-400 text-sm mt-1">Try adjusting your search criteria</p>
      </motion.div>
    );
  }

  return (
    <div className="p-6">
      {/* Select All Header - Only show when bulk edit is active */}
      {showBulkEdit && (
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedClinicians.length === clinicians.length && clinicians.length > 0}
              onChange={onSelectAll}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">
              Select All ({clinicians.length})
            </span>
          </label>
        </div>
      )}

      {/* Clinicians List */}
      <div className="space-y-4">
        {processedClinicians.map((clinician, index) => (
          <ClinicianRow
            key={clinician._id || index}
            clinician={clinician}
            variants={itemVariants}
            isSelected={selectedClinicians.includes(clinician._id || clinician.id || '')}
            onSelect={handleSelectClinician}
            onStatusToggle={handleStatusToggle}
            isTogglingStatus={togglingStatus.has(clinician._id || clinician.id || '')}
            navigate={navigate}
            showBulkEdit={showBulkEdit}
          />
        ))}
      </div>


    </div>
  );
});

// Enhanced row component with improved layout
const ClinicianRow = memo<{
  clinician: ProcessedClinician;
  variants: typeof itemVariants;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onStatusToggle?: (clinicianId: string, currentStatus: boolean) => void;
  isTogglingStatus?: boolean;
  navigate: NavigateFunction;
  showBulkEdit?: boolean;
}>(({ clinician, variants, isSelected, onSelect, onStatusToggle, isTogglingStatus = false, navigate, showBulkEdit = false }) => (
  <motion.div
    variants={variants}
    className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${
      isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
    }`}
  >
    {/* Selection Checkbox - Only show when bulk edit is active */}
    {showBulkEdit && (
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onSelect(clinician._id || clinician.id || '')}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
    )}

    {/* Avatar */}
    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-gray-700 font-semibold ${
      clinician.displayFirstName.startsWith('A') ? 'bg-purple-100' :
      clinician.displayFirstName.startsWith('B') ? 'bg-blue-100' :
      clinician.displayFirstName.startsWith('C') ? 'bg-green-100' :
      clinician.displayFirstName.startsWith('D') ? 'bg-yellow-100' :
      clinician.displayFirstName.startsWith('E') ? 'bg-pink-100' :
      clinician.displayFirstName.startsWith('F') ? 'bg-indigo-100' :
      clinician.displayFirstName.startsWith('G') ? 'bg-red-100' :
      clinician.displayFirstName.startsWith('H') ? 'bg-orange-100' :
      clinician.displayFirstName.startsWith('I') ? 'bg-teal-100' :
      clinician.displayFirstName.startsWith('J') ? 'bg-cyan-100' :
      clinician.displayFirstName.startsWith('K') ? 'bg-lime-100' :
      clinician.displayFirstName.startsWith('L') ? 'bg-emerald-100' :
      clinician.displayFirstName.startsWith('M') ? 'bg-violet-100' :
      clinician.displayFirstName.startsWith('N') ? 'bg-fuchsia-100' :
      clinician.displayFirstName.startsWith('O') ? 'bg-rose-100' :
      clinician.displayFirstName.startsWith('P') ? 'bg-sky-100' :
      clinician.displayFirstName.startsWith('Q') ? 'bg-slate-100' :
      clinician.displayFirstName.startsWith('R') ? 'bg-stone-100' :
      clinician.displayFirstName.startsWith('S') ? 'bg-zinc-100' :
      clinician.displayFirstName.startsWith('T') ? 'bg-neutral-100' :
      clinician.displayFirstName.startsWith('U') ? 'bg-gray-100' :
      clinician.displayFirstName.startsWith('V') ? 'bg-slate-100' :
      clinician.displayFirstName.startsWith('W') ? 'bg-gray-100' :
      clinician.displayFirstName.startsWith('X') ? 'bg-slate-100' :
      clinician.displayFirstName.startsWith('Y') ? 'bg-gray-100' :
      'bg-purple-100'
    }`}>
      {clinician.initials}
    </div>

    {/* Main Info */}
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <Link to={`/practice/clinician/details/${clinician.id || clinician._id}`}>
            <h3 className="font-medium text-gray-900"><strong>{clinician.displayName}</strong></h3>
        </Link>
        {/* <span className={`px-2 py-1 text-xs rounded-full ${
          clinician.isActive 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {clinician.isActive ? 'Active' : 'Inactive'}
        </span> */}
      </div>
      <p className="text-sm text-gray-500">
        {clinician.title || 'LMFT, MFCC'}
      </p>
    </div>

    {/* Completion & Availability */}
    <div className="flex flex-col items-end gap-1 min-w-[140px]">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500 font-medium">Completion:</span>
        <span className={`font-semibold ${
          (clinician.profileCompletionPercentage ?? 0) >= 90 ? 'text-green-600' :
          (clinician.profileCompletionPercentage ?? 0) >= 70 ? 'text-amber-600' :
          'text-red-600'
        }`}>
          {clinician.profileCompletionPercentage ?? 0}%
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500 font-medium">Availability:</span>
        <span className={`font-semibold ${
          (clinician.availabilityMetrics.availabilityPercentage ?? 0) > 0 ? 'text-emerald-600' : 'text-gray-500'
        }`}>
          {clinician.availabilityMetrics.openSlots > 0
            ? `${clinician.availabilityMetrics.openSlots} open (${clinician.availabilityMetrics.availabilityPercentage}%)`
            : clinician.availabilityMetrics.totalSlots > 0
              ? `0 open (${clinician.availabilityMetrics.availabilityPercentage}%)`
              : 'No data'
          }
        </span>
      </div>
      <p className="text-sm text-gray-500 mt-0.5">
        {clinician.specialtyDisplay || '—'}
      </p>
    </div>

    {/* Actions */}
    <div className="flex items-center gap-2">
      {/* Status Toggle Switch */}
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={clinician.isActive}
          onChange={() => {
            onStatusToggle?.(clinician.id || clinician._id, clinician.isActive);
          }}
          disabled={isTogglingStatus}
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>

      {/* Edit Button */}
      <button
        onClick={() => navigate(`/practice/clinicians/edit/${clinician.id || clinician._id}`)}
        className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 transition-colors"
      >
        Edit
      </button>
    </div>
  </motion.div>
));

// Bulk Edit Modal Component
const BulkEditModal = memo<{
  clinicians: ProcessedClinician[];
  onClose: () => void;
  onUpdate: (updates: BulkUpdateData) => Promise<void>;
  isUpdating: boolean;
}>(({ clinicians, onClose, onUpdate, isUpdating }) => {
  const [updates, setUpdates] = useState<BulkUpdateData>({});
  const [activeTab, setActiveTab] = useState<'status' | 'age' | 'location' | 'insurance'>('status');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUpdate(updates);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Bulk Edit Clinicians</h2>
              <p className="text-sm text-gray-600 mt-1">
                Editing {clinicians.length} clinician{clinicians.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-6">
            {[
              { key: 'status', label: 'Status', icon: CheckCircle },
              { key: 'age', label: 'Age Range', icon: Users },
              { key: 'location', label: 'Location', icon: Check },
              { key: 'insurance', label: 'Insurance', icon: Check }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as 'status' | 'age' | 'location' | 'insurance')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
          
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Status Tab */}
            {activeTab === 'status' && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Update Status</h3>
                <div className="grid grid-cols-2 gap-3">
                  {(['active', 'inactive'] as const).map(status => (
                    <label key={status} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="status"
                        value={status}
                        checked={updates.status === status}
                        onChange={(e) => setUpdates(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                        className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700 capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Age Range Tab */}
            {activeTab === 'age' && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Update Age Range</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Age
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={updates.ageRange?.min || ''}
                      onChange={(e) => setUpdates(prev => ({
                        ...prev,
                        ageRange: { ...prev.ageRange, min: parseInt(e.target.value) || 0, max: prev.ageRange?.max || 100 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Age
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={updates.ageRange?.max || ''}
                      onChange={(e) => setUpdates(prev => ({
                        ...prev,
                        ageRange: { ...prev.ageRange, min: prev.ageRange?.min || 0, max: parseInt(e.target.value) || 100 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Update Location</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={updates.location?.city || ''}
                      onChange={(e) => setUpdates(prev => ({
                        ...prev,
                        location: { ...prev.location, city: e.target.value, state: prev.location?.state || '', zipCode: prev.location?.zipCode || '' }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={updates.location?.state || ''}
                      onChange={(e) => setUpdates(prev => ({
                        ...prev,
                        location: { ...prev.location, city: prev.location?.city || '', state: e.target.value, zipCode: prev.location?.zipCode || '' }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={updates.location?.zipCode || ''}
                    onChange={(e) => setUpdates(prev => ({
                      ...prev,
                      location: { ...prev.location, city: prev.location?.city || '', state: prev.location?.state || '', zipCode: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            )}

            {/* Insurance Tab */}
            {activeTab === 'insurance' && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Update Insurance</h3>
                <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                  {INSURANCE_OPTIONS.map(insurance => (
                    <label key={insurance} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={updates.insurance?.includes(insurance) || false}
                        onChange={(e) => {
                          const currentInsurance = updates.insurance || [];
                          if (e.target.checked) {
                            setUpdates(prev => ({
                              ...prev,
                              insurance: [...currentInsurance, insurance]
                            }));
                          } else {
                            setUpdates(prev => ({
                              ...prev,
                              insurance: currentInsurance.filter(i => i !== insurance)
                            }));
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{insurance}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdating || Object.keys(updates).length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? 'Updating...' : 'Update Clinicians'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
});

// Reusable Status Badge Component
const StatusBadge = memo<{
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  label: string;
}>(({ icon, bgColor, textColor, label }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bgColor} ${textColor}`}>
    {icon}
    {label}
  </span>
));

// Reusable Action Button Component
const ActionButton = memo<{
  to: string;
  icon: React.ReactNode;
  label: string;
  variant: 'primary' | 'secondary' | 'outline';
}>(({ to, icon, label, variant }) => {
  const baseClasses = "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1";
  
  const variantClasses = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500",
    outline: "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500 shadow-sm"
  };

  return (
    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      <Link
        to={to}
        className={`${baseClasses} ${variantClasses[variant]}`}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
      </Link>
    </motion.div>
  );
});

// Utility functions
function getInitials(clinician: Clinician): string {
  // Handle both camelCase and snake_case field names
  const firstName = (clinician.firstName || clinician.first_name || '') as string;
  const lastName = (clinician.lastName || clinician.last_name || '') as string;
  
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (clinician.fullName) {
    const names = clinician.fullName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  }
  if (clinician.name) {
    const names = clinician.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  }
  if (clinician.email) {
    return clinician.email.substring(0, 2).toUpperCase();
  }
  return 'CL';
}

ClinicianTable.displayName = 'ClinicianTable';
ClinicianRow.displayName = 'ClinicianRow';
StatusBadge.displayName = 'StatusBadge';
ActionButton.displayName = 'ActionButton';
BulkEditModal.displayName = 'BulkEditModal';

export default ClinicianTable;
